/**
 * <Tabs> — une rangée d'onglets soulignés : elle change la vue d'une même chose (molecule).
 *
 * Les vues d'une activité, les parties d'une fiche, les listes d'une page : des libellés seuls, sans
 * fond ni contour, un filet sous toute la rangée, l'onglet ouvert à l'encre du texte et souligné d'un
 * trait de 3 px à la couleur de l'univers (`role` : teal chez le tuteur, orange chez l'élève, marine
 * chez le parent), les autres en gris. Ce n'est pas une pilule de sélection (`<ChoiceChip>`, qui
 * répond à une question) ni un contrôle segmenté (qui pose un mode) : en pilules, des onglets se
 * lisaient comme des boutons empilés (Wajih, 29/10).
 *
 *  - `md` (44 px, libellé à 15) pour une page, `sm` (36 px, à 14) pour une rangée dans un panneau ;
 *    toute la cellule presse, 44 px de cible au moins.
 *  - Un compte en pastille après le libellé (`count` : rouge pour ce qui attend, sinon neutre), un
 *    onglet désactivé, et `trailing` au bout de la rangée pour une bascule liée (le « côte à côte »
 *    du live).
 *  - Trop d'onglets : la rangée défile, un fondu au bord dit qu'il y en a d'autres (`fadeColor`, la
 *    surface sur laquelle la rangée est posée) ; jamais de chevrons ni de seconde ligne.
 *  - Le trait glisse d'un onglet à l'autre (`standard`, la courbe de la marque) ; en mouvement réduit,
 *    il saute.
 *  - `tablist` / `tab` / `aria-selected`, l'anneau jaune au clavier ; au web, seul l'onglet ouvert est
 *    dans l'ordre de tabulation, et les flèches gauche et droite ouvrent le voisin et y portent le focus
 *    (le motif d'onglets de l'ARIA).
 *
 * Contrôlé : l'onglet ouvert arrive en prop, le toucher repart.
 */
import React, { useEffect, useMemo, useRef, useState } from "react"
import { Animated, Easing, type LayoutChangeEvent, Platform, Pressable, View } from "react-native"
import { LinearGradient } from "expo-linear-gradient"
import { StyleSheet, useUnistyles } from "react-native-unistyles"

import { Badge } from "../../atoms/Badge"
import { FocusRing, focusRingHost } from "../../atoms/FocusRing"
import { HorizontalList } from "../../atoms/HorizontalList"
import { Text } from "../../atoms/typography/Text"
import { useReduceMotion } from "../../hooks/useReduceMotion"
import { type SemanticColorKey } from "../../theme/tokens/colors"
import { cappedCount } from "../../utils/cappedCount"
import { hexWithAlpha } from "../../utils/shadow"
import { slopTo } from "../../utils/touchTarget"
import { tabOffset } from "./tabsGeometry"
import { nextTab, tabStopIndex } from "./tabsKeyboard"

export type TabItem<K extends string> = {
    key: K
    label: string
    /** Un compte en pastille après le libellé. Absent ou 0 : rien. */
    count?: number
    /** `danger` (défaut) pour ce qui attend (des non-lus, des points corrigés), `neutral` sinon. */
    countTone?: "danger" | "neutral"
    disabled?: boolean
}

export type TabsProps<K extends string> = {
    items: TabItem<K>[]
    value: K
    onChange: (key: K) => void
    /** `md` (défaut) : une page ; `sm` : une rangée dans un panneau. */
    size?: "md" | "sm"
    /** Au bout de la rangée, hors du défilement : une bascule liée aux vues. */
    trailing?: React.ReactNode
    /** La surface sous la rangée, d'où part le fondu quand elle déborde. Défaut `surface`. */
    fadeColor?: SemanticColorKey
    /** Le nom de la rangée, pour un lecteur d'écran. */
    accessibilityLabel?: string
    /**
     * La rangée prend la largeur de ses onglets au lieu de remplir sa place : une courte rangée posée à côté
     * d'une autre, dans une boîte sans largeur à elle. Sans elle, une telle rangée mesure zéro au natif (le web
     * la mesure à son contenu). Défaut `false`.
     */
    fit?: boolean
    testID?: string
}

/** `<Pressable>` ne type que `pressed` ; react-native-web ajoute `focused` et `hovered` à l'exécution. */
type PressableVisualState = { pressed: boolean; focused?: boolean; hovered?: boolean }

const SIZES = {
    md: { height: 44, text: "text-base", gap: "6" },
    sm: { height: 36, text: "text-md", gap: "5" },
} as const

/** Les mesures d'une rangée d'onglets — sa hauteur (filet compris), son écart, son texte : son squelette les lit. */
export function tabsRowMetrics(size: "md" | "sm" = "md") {
    const { height, gap, text } = SIZES[size]
    return { height: height + 1, gap, text }
}

const UNDERLINE = 3
const FADE_WIDTH = 32

export function Tabs<K extends string>(props: TabsProps<K>) {
    const {
        items,
        value,
        onChange,
        size = "md",
        trailing,
        fadeColor = "surface",
        accessibilityLabel,
        fit = false,
        testID,
    } = props
    const { theme } = useUnistyles()
    const reduceMotion = useReduceMotion()
    const spec = SIZES[size]

    // État d'interface : la largeur de chaque onglet (le trait se pose sous lui, à la place que dit la rangée :
    // `tabOffset`), et si la rangée déborde — un booléen, pour ne se redessiner qu'au passage du bord, pas à
    // chaque pixel d'un redimensionnement.
    const [widths, setWidths] = useState<Partial<Record<K, number>>>({})
    const [overflows, setOverflows] = useState(false)
    const visibleWidth = useRef(0)
    const rowEnd = useRef(0)
    const checkOverflow = () => setOverflows(visibleWidth.current > 0 && rowEnd.current > visibleWidth.current)
    const left = useRef(new Animated.Value(0)).current
    const width = useRef(new Animated.Value(0)).current
    const placed = useRef(false)
    // Les nœuds des onglets : la flèche du clavier porte le focus sur celui qu'elle ouvre.
    const nodes = useRef(new Map<K, View>())

    const keys = items.map((item) => item.key)
    const gap = theme.spacing[spec.gap]
    const offset = tabOffset(keys, widths, gap, value)
    // Une identité stable tant que la place ne bouge pas : l'effet du trait ne rejoue qu'à un vrai déplacement.
    const offsetX = offset?.x
    const offsetWidth = offset?.width
    // Le bout de la rangée, comme le trait : des largeurs, jamais d'une position que le web ne remesure pas.
    const lastKey = keys[keys.length - 1]
    const last = lastKey != null ? tabOffset(keys, widths, gap, lastKey) : null
    const rowEndValue = last != null ? last.x + last.width : null
    useEffect(() => {
        if (rowEndValue == null) return
        rowEnd.current = rowEndValue
        checkOverflow()
        // `checkOverflow` relit des refs : seul le bout compte.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rowEndValue])
    const current = useMemo(
        () => (offsetX == null || offsetWidth == null ? null : { x: offsetX, width: offsetWidth }),
        [offsetX, offsetWidth]
    )
    useEffect(() => {
        if (current == null) return undefined
        if (!placed.current || reduceMotion) {
            placed.current = true
            left.setValue(current.x)
            width.setValue(current.width)
            return undefined
        }
        const timing = (target: Animated.Value, toValue: number) =>
            Animated.timing(target, {
                toValue,
                duration: theme.motion.standard,
                easing: Easing.bezier(...theme.motion.easing),
                // Le trait glisse et s'étire par `transform` : le pilote natif le porte (pas le web).
                useNativeDriver: Platform.OS !== "web",
            })
        const move = Animated.parallel([timing(left, current.x), timing(width, current.width)])
        move.start()
        return () => move.stop()
    }, [current, reduceMotion, left, width, theme.motion])

    const measure = (key: K) => (event: LayoutChangeEvent) => {
        const w = event.nativeEvent.layout.width
        setWidths((before) => (before[key] === w ? before : { ...before, [key]: w }))
    }

    /**
     * Le trait : 1 px de large, étiré (`scaleX`) à la largeur de l'onglet ouvert et glissé (`translateX`)
     * sous lui. `scaleX` étire depuis son milieu : le glissement l'y compense d'une demi-largeur.
     */
    const translateX = useMemo(
        () => Animated.add(left, Animated.add(Animated.multiply(width, 0.5), -0.5)),
        [left, width]
    )

    const step = (direction: 1 | -1) => {
        const next = nextTab(items, value, direction)
        if (next == null) return
        onChange(next)
        nodes.current.get(next)?.focus?.()
    }
    // Les flèches du clavier (le web) : react-native-web passe `onKeyDown` au nœud de la rangée.
    const keyboard =
        Platform.OS === "web"
            ? {
                  onKeyDown: (event: { key: string; preventDefault: () => void }) => {
                      if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return
                      event.preventDefault()
                      step(event.key === "ArrowRight" ? 1 : -1)
                  },
              }
            : {}

    return (
        <View style={styles.root(spec.height)} testID={testID}>
            <View
                style={styles.scroller(fit)}
                onLayout={(event) => {
                    visibleWidth.current = event.nativeEvent.layout.width
                    checkOverflow()
                }}
                accessibilityRole="tablist"
                accessibilityLabel={accessibilityLabel}
                {...keyboard}
            >
                <HorizontalList gap={spec.gap} padding="0">
                    {items.map((item) => {
                        const selected = item.key === value
                        const disabled = item.disabled === true
                        const count = item.count ?? 0
                        return (
                            <Pressable
                                key={item.key}
                                ref={(node) => {
                                    if (node != null) nodes.current.set(item.key, node)
                                    else nodes.current.delete(item.key)
                                }}
                                onLayout={measure(item.key)}
                                // Au web, un seul arrêt de tabulation : l'onglet ouvert ; les flèches font le reste.
                                tabIndex={Platform.OS === "web" ? tabStopIndex(selected) : undefined}
                                onPress={selected ? undefined : () => onChange(item.key)}
                                disabled={disabled}
                                accessibilityRole="tab"
                                accessibilityState={{ selected, disabled }}
                                // Le web ne lit pas `accessibilityState` : l'onglet ouvert s'y dit par l'attribut ARIA.
                                aria-selected={selected}
                                accessibilityLabel={count > 0 ? `${item.label}, ${count}` : item.label}
                                hitSlop={{ top: slopTo(spec.height), bottom: slopTo(spec.height) }}
                                style={[styles.tab(spec.height), focusRingHost]}
                            >
                                {({ focused, hovered }: PressableVisualState) => (
                                    <>
                                        <FocusRing visible={focused === true} radius={theme.radius.sm} />
                                        <Text
                                            size={spec.text}
                                            weight="semibold"
                                            color={selected || hovered === true ? "text-strong" : "text-muted"}
                                            numberOfLines={1}
                                            style={disabled ? styles.disabled : undefined}
                                        >
                                            {item.label}
                                        </Text>
                                        {count > 0 && (
                                            // La chip se cale en haut de sa rangée : sa boîte la remet au milieu.
                                            <View style={styles.count}>
                                                <Badge
                                                    text={cappedCount(count)}
                                                    variant={item.countTone === "neutral" ? "neutral" : "danger"}
                                                    tinted
                                                    truncate={false}
                                                />
                                            </View>
                                        )}
                                    </>
                                )}
                            </Pressable>
                        )
                    })}
                    {current != null && (
                        <Animated.View
                            pointerEvents="none"
                            style={[styles.underline, { transform: [{ translateX }, { scaleX: width }] }]}
                        />
                    )}
                </HorizontalList>
                {overflows && (
                    <View style={styles.fade} pointerEvents="none">
                        <LinearGradient
                            colors={[
                                hexWithAlpha(theme.colors[fadeColor], 0) ?? "transparent",
                                theme.colors[fadeColor],
                            ]}
                            start={{ x: 0, y: 0 }}
                            end={{ x: 1, y: 0 }}
                            style={styles.fill}
                        />
                    </View>
                )}
            </View>
            {trailing != null && <View style={styles.trailing}>{trailing}</View>}
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    root: (height: number) => ({
        flexDirection: "row" as const,
        alignItems: "stretch" as const,
        minHeight: height,
        flexGrow: 0,
        flexShrink: 0,
        borderBottomWidth: 1,
        borderBottomColor: theme.colors["border-subtle"],
    }),
    // `fit` : la largeur de ses onglets (base `auto`, sans grandir) ; sinon toute la place (base 0, grandit).
    scroller: (fit: boolean) => ({ flexGrow: fit ? 0 : 1, flexShrink: 1, flexBasis: fit ? "auto" : 0, minWidth: 0 }),
    tab: (height: number) => ({
        height,
        flexDirection: "row" as const,
        alignItems: "center" as const,
        gap: theme.spacing["2"],
    }),
    underline: {
        position: "absolute",
        left: 0,
        bottom: 0,
        width: 1,
        height: UNDERLINE,
        backgroundColor: theme.colors.role,
    },
    disabled: { opacity: 0.5 },
    count: { justifyContent: "center" },
    fade: { position: "absolute", top: 0, right: 0, bottom: 0, width: FADE_WIDTH },
    fill: { flex: 1 },
    trailing: { flexDirection: "row", alignItems: "center", paddingLeft: theme.spacing["3"] },
}))
